import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    GoogleReCaptchaProvider,
    useGoogleReCaptcha,
} from 'react-google-recaptcha-v3';

import { Brand } from '../brand';
import { Button, alert, AlertType } from '@quyva/shared-ui';
import { PoweredByInfo } from '../poweredByComponent';
import { faCheck} from '@fortawesome/free-solid-svg-icons';
import { AuthStatus, RouterPath } from '@quyva/common';
import axios from 'axios';
import { getUrl } from '../../utils';
import { Config } from '../../config/envConfig';

const CaptchaFormComponent = ({
    name,
    setName,
    setAuthState,
    vendorDetails,
    vendorId,
    isSmallScreen,
}) => {
    const [token, setToken] = useState('');
    const [searchParams] = useSearchParams(); // Extract query parameters
    const navigate = useNavigate();

    const hasAudioParams = searchParams.get('audio') === 'true';

    const { executeRecaptcha } = useGoogleReCaptcha();
    const buttonRef = useRef(null);

    const consumeSuppressRecaptchaErrorAlert = () => {
        const suppress =
            sessionStorage.getItem('suppressRecaptchaErrorAlert') === 'true';
        if (suppress) {
            sessionStorage.removeItem('suppressRecaptchaErrorAlert');
        }
        return suppress;
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            buttonRef?.current?.click(); // Programmatically click the button
        }
    };

    // Create an event handler so you can call the verification on button click event or form submit
    const handleReCaptchaVerify = useCallback(async () => {
        try {
            if (!executeRecaptcha) {
                console.log('Execute recaptcha not yet available');
                return;
            }

            const token = await executeRecaptcha('getAuthToken');
            setToken(token);
        } catch (error) {
            throw new Error(error);
        }
    }, [executeRecaptcha]);

    // You can use useEffect to trigger the verification as soon as the component being loaded
    useEffect(() => {
        (async () => {
            try {
                await handleReCaptchaVerify();
            } catch (error) {
                const suppress = consumeSuppressRecaptchaErrorAlert();
                if (suppress) {
                    console.error(
                        'Error fetching reCAPTCHA token after session expiry redirect',
                        error
                    );
                } else {
                    alert(
                        AlertType.ERROR,
                        'Error fetching reCAPTCHA token. Please refresh the page and try again!'
                    );
                }
            }
        })();
    }, [handleReCaptchaVerify]);

    const handleSubmit = async (e) => {
        try {
            if (e) {
                e.preventDefault();
            }

            try {
                await handleReCaptchaVerify();
            } catch (error) {
                const suppress = consumeSuppressRecaptchaErrorAlert();
                console.error('Error during captcha token generation', error);
                if (suppress) {
                    return;
                }
                throw new Error(
                    'An error occurred during captcha token generation. Please try again.'
                );
            }

            try {
                if (token) {
                    const formData = new FormData();
                    formData.append('token', token);
                    formData.append('user_uuid', vendorId);

                    const dynamicUrl = getUrl(
                        `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/customer_chat_routes/verify-recaptcha`
                    );

                    // Send the FormData
                    const response = await axios.post(dynamicUrl, formData, {});

                    const jwtToken = response.data.token;

                    const productSupportUserData = JSON.parse(
                        localStorage.getItem('productSupport')
                    );

                    if (productSupportUserData[vendorId]) {
                        productSupportUserData[vendorId] = {
                            ...productSupportUserData[vendorId],
                            id: vendorId,
                            token: jwtToken,
                        };
                    } else {
                        productSupportUserData[vendorId] = {
                            id: vendorId,
                            token: jwtToken,
                        };
                    }

                    localStorage.setItem(
                        'productSupport',
                        JSON.stringify(productSupportUserData)
                    );

                    setAuthState(AuthStatus.AUTHENTICATED);
                    const queryParams = [];
                    if (hasAudioParams) queryParams.push('audio=true');

                    const queryString =
                        queryParams.length > 0
                            ? `?${queryParams.join('&')}`
                            : '';
                    navigate(
                        `/${RouterPath.CHAT_SUPPORT}/${vendorId}${queryString}`,
                        {
                            replace: true,
                        }
                    );
                }
            } catch (error) {
                console.error('CAPTCHA verification failed', error);
                throw new Error(
                    'CAPTCHA verification failed. Please try again.'
                );
            }
        } catch (error) {
            const suppress = consumeSuppressRecaptchaErrorAlert();
            if (suppress) {
                console.error('Captcha flow error after session expiry redirect', error);
                return;
            }
            alert(AlertType.ERROR, error.message);
        }
    };

    return (
        <div className='w-full flex items-center justify-center min-h-screen bg-gradient-primary select-none p-6'>
            <div className='w-full max-w-[500px] flex-col border bg-backgrounds-white p-6 shadow-lg rounded-xl'>
                {vendorDetails && (
                    <div className='w-full flex justify-center items-center mx-auto'>
                        <Brand
                            iconClassName={'w-10 h-10 mr-0'}
                            vendorDetails={vendorDetails}
                            showCopyRightText={false}
                        />
                    </div>
                )}
                <div className='flex flex-col text-sm w-full'>
                    <div className='py-6'>
                        <div className='w-full text-center text-texts-secondary300'>
                            Verify you are not a robot.
                        </div>

                        <Button
                            ref={buttonRef}
                            text={'Verify'}
                            className='w-full h-11 mt-8'
                            onClick={async (e) => {
                                await handleSubmit(e);
                            }}
                            icon={faCheck}
                            type={'success'}
                            hoverText={'Verify'}
                            disabled={false}
                            active={true}
                        />
                    </div>
                </div>

                <div className='w-full flex justify-center items-center'>
                    <PoweredByInfo />
                </div>
            </div>
        </div>
    );
};

const CaptchaComponent = ({
    name,
    setName,
    setAuthState,
    vendorDetails,
    fetchVendorDetails,
    verifyAuthentication,
    isSmallScreen,
}) => {
    const { vendorId } = useParams(); // Extract the vendorId from the URL path
    const navigate = useNavigate();
    const captchaKey = Config.CAPTCHA_SITE_KEY;

    useEffect(() => {
        verifyAuthentication(vendorId);
        return () => {};
    }, [vendorId]);

    useEffect(() => {
        if (!vendorId) {
            navigate(`/${RouterPath.NOT_FOUND}`);
            return;
        }

        if (!vendorDetails) {
            (async () => {
                const isFetched = await fetchVendorDetails(vendorId);
                if (!isFetched) {
                    navigate(`/${RouterPath.NOT_FOUND}`);
                }
            })();
            return;
        }
    }, [vendorDetails, vendorId]);

    if (!captchaKey) {
        alert(AlertType.ERROR, 'Captcha authentication failed!');
        return <></>;
    }

    return (
        <GoogleReCaptchaProvider reCaptchaKey={captchaKey}>
            <CaptchaFormComponent
                name={name}
                setName={setName}
                setAuthState={setAuthState}
                vendorDetails={vendorDetails}
                vendorId={vendorId}
                isSmallScreen={isSmallScreen}
            />
        </GoogleReCaptchaProvider>
    );
};

export default CaptchaComponent;
